import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
  InputAdornment,
  CircularProgress,
  Box,
} from "@mui/material";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../../../../context";
import LoadingButton from "@mui/lab/LoadingButton";
import useFetchObjects from "../../../../../api/useFetchObjects";
import useAdd from "../../../../../api/useAdd";
import { useTranslation } from "react-i18next";
import SearchIcon from "@mui/icons-material/Search";

// Debounce function
function debounce(func, delay) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

const AddAppointment = ({ open, onClose }) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const user = auth?.user;
  const token = user?.token;

  // Utility function to get the current time and date
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    patient: null,
    doctor: "",
    sickness_history: "",
    timeOfVisit: getCurrentTime(),
    dateOfVisit: getCurrentDate(),
    due: "",
    fees: "",
  });

  const [formErrors, setFormErrors] = useState({});

  // Patient search states
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [searchTerm, setSearchTerm] = useState("");
  const [allPatients, setAllPatients] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [patientInputValue, setPatientInputValue] = useState("");
  const [forceUpdate, setForceUpdate] = useState(0);

  const { data: users } = useFetchObjects(["UsersList"], `users/`, token);

  const {
    data: patientsData,
    isLoading: isLoadingPatients,
    refetch,
  } = useFetchObjects(
    ["patients", page, pageSize, searchTerm, forceUpdate],
    `patients/?page=${page}&page_size=${pageSize}&search=${searchTerm}`,
    token
  );

  const { handleAdd } = useAdd("appointments", token);

  useEffect(() => {
    if (patientsData?.results) {
      setAllPatients((prevPatients) => {
        const newPatients = [...prevPatients, ...patientsData.results];
        // Remove duplicates based on patient ID
        const uniquePatients = newPatients.reduce((acc, current) => {
          if (!acc.find((item) => item.id === current.id)) {
            acc.push(current);
          }
          return acc;
        }, []);
        return uniquePatients;
      });
      setHasMore(patientsData.next !== null);
    } else {
      setHasMore(false);
    }
  }, [patientsData]);

  useEffect(() => {
    // Fetch initial data when component mounts
    if (!searchTerm) {
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetch();
    }
  }, []);

  useEffect(() => {
    if (searchTerm !== "") {
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetch();
    } else {
      // When searchTerm is cleared, fetch initial data
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
      refetch();
    }
  }, [searchTerm, refetch]);

  const handleSubmit = (e) => {
    e.preventDefault();
    let errors = {};
    Object.keys(formData).forEach((key) => {
      if (!formData[key] && key !== "patient") {
        errors[key] = t("reception.requiredField");
      }
    });

    if (!formData.patient) {
      errors.patient = t("reception.requiredField");
    }

    if (Object.keys(errors).length === 0) {
      const appointmentData = new FormData();
      Object.keys(formData).forEach((key) => {
        if (key === "patient") {
          appointmentData.append(key, formData[key]?.id);
        } else {
          appointmentData.append(key, formData[key]);
        }
      });
      handleAdd(appointmentData);
      onClose();
    } else {
      setFormErrors(errors);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setFormErrors({ ...formErrors, [e.target.name]: "" });
  };

  // Patient Autocomplete Handlers
  const handlePatientInputChange = (event, newInputValue) => {
    setPatientInputValue(newInputValue);
    debouncedSetSearchTerm(newInputValue);
  };

  const handlePatientChange = (event, newValue) => {
    setFormData({ ...formData, patient: newValue });
    setFormErrors({ ...formErrors, patient: "" });
  };

  const handleScroll = (event) => {
    const { scrollTop, clientHeight, scrollHeight } = event.target;
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      hasMore &&
      !isLoadingPatients
    ) {
      setPage((prevPage) => prevPage + 1);
    }
  };

  // Debounced setSearchTerm
  const debouncedSetSearchTerm = useCallback(
    debounce((value) => {
      setSearchTerm(value);
    }, 300),
    []
  );

  // Function to force update
  const forceUpdateAutocomplete = () => {
    setForceUpdate((prev) => prev + 1);
  };

  useEffect(() => {
    if (searchTerm === "") {
      forceUpdateAutocomplete();
    }
  }, [searchTerm]);

  // Reset form when dialog is closed
  useEffect(() => {
    if (!open) {
      setFormData({
        patient: null,
        doctor: "",
        sickness_history: "",
        timeOfVisit: getCurrentTime(),
        dateOfVisit: getCurrentDate(),
        due: "",
        fees: "",
      });
      setFormErrors({});
      setPatientInputValue("");
      setAllPatients([]);
      setPage(1);
      setHasMore(true);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <form onSubmit={handleSubmit}>
        <DialogTitle
          style={{
            backgroundColor: "#3f51b5",
            color: "white",
            textAlign: "center",
          }}
        >
          <Typography variant="h6">{t("reception.createOPD")}</Typography>
        </DialogTitle>
        <DialogContent style={{ padding: "2rem" }}>
          <Grid spacing={4} container>
            <Grid xs={12} md={6} item>
              <Autocomplete
                options={allPatients}
                getOptionLabel={(option) =>
                  `${option.firstname} ${option.lastname || ""}  Phone: ${
                    option.phone || "N/A"
                  }`.trim()
                }
                inputValue={patientInputValue}
                onInputChange={handlePatientInputChange}
                onChange={handlePatientChange}
                value={formData.patient}
                ListboxProps={{
                  onScroll: handleScroll,
                }}
                size="small"
                loading={isLoadingPatients}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("reception.patientName")}
                    variant="outlined"
                    error={!!formErrors.patient}
                    helperText={formErrors.patient}
                    placeholder={t(
                      "reception.searchPatientPlaceholder",
                      "Search Patient name or phone"
                    )}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          {isLoadingPatients ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid xs={12} md={6} item>
              <TextField
                select
                autoFocus
                size="small"
                label={t("reception.doctor")}
                name="doctor"
                type="text"
                fullWidth
                value={formData.doctor}
                onChange={handleChange}
                variant="outlined"
                error={!!formErrors.doctor}
                helperText={formErrors.doctor}
                required
              >
                {users &&
                  users.map((doctor) => (
                    <MenuItem value={doctor.id} key={doctor.id}>
                      {doctor.first_name} {doctor.last_name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid xs={12} item>
              <TextField
                autoFocus
                size="small"
                label={t("reception.sickness_history")}
                name="sickness_history"
                type="text"
                fullWidth
                value={formData.sickness_history}
                onChange={handleChange}
                variant="outlined"
                multiline
                rows={2}
                error={!!formErrors.sickness_history}
                helperText={formErrors.sickness_history}
                required
              />
            </Grid>
            <Grid xs={12} md={6} item>
              <TextField
                autoFocus
                size="small"
                label={t("reception.timeOfVisit")}
                name="timeOfVisit"
                type="time"
                fullWidth
                onChange={handleChange}
                value={formData.timeOfVisit}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Grid>
            <Grid xs={12} md={6} item>
              <TextField
                autoFocus
                size="small"
                label={t("reception.dateOfVisit")}
                name="dateOfVisit"
                type="date"
                fullWidth
                onChange={handleChange}
                value={formData.dateOfVisit}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                required
              />
            </Grid>
            <Grid xs={12} md={6} item>
              <TextField
                autoFocus
                size="small"
                label={t("reception.due")}
                name="due"
                type="number"
                fullWidth
                value={formData.due}
                onChange={handleChange}
                variant="outlined"
                error={!!formErrors.due}
                helperText={formErrors.due}
                required
              />
            </Grid>
            <Grid xs={12} md={6} item>
              <TextField
                autoFocus
                size="small"
                label={t("reception.fees")}
                name="fees"
                type="number"
                fullWidth
                value={formData.fees}
                onChange={handleChange}
                variant="outlined"
                error={!!formErrors.fees}
                helperText={formErrors.fees}
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions
          style={{ justifyContent: "center", paddingBottom: "20px" }}
        >
          <Button
            onClick={onClose}
            color="primary"
            variant="outlined"
            style={{ margin: "0px 10px" }}
          >
            {t("reception.cancel")}
          </Button>
          <LoadingButton type="submit" color="primary" variant="contained">
            {t("reception.save")}
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddAppointment;
